/**
 * Token Bridge Service
 * Connects Logto authentication to API requests
 */

// Token provider function type
type TokenProvider = (resource?: string) => Promise<string | undefined>

// Token provider (set by React component)
let tokenProvider: TokenProvider | null = null

import { AppConfigService } from './config'

// API Resource identifier
let apiResource = AppConfigService.getString('VITE_LOGTO_API_RESOURCE') || ''

/**
 * Set token provider (called by useLogtoToken hook)
 */
export function setTokenProvider(provider: TokenProvider | null): void {
  tokenProvider = provider
}

/**
 * Set API Resource (for runtime configuration)
 */
export function setApiResource(resource: string): void {
  apiResource = resource
}

/**
 * Clear token provider (on logout)
 */
export function clearAccessToken(): void {
  tokenProvider = null
}

/**
 * Get userinfo access token (default token, no resource).
 * Use for Logto /me API and sync_userinfo endpoint.
 */
export async function getUserinfoAccessToken(): Promise<string | null> {
  if (!tokenProvider) return null
  try {
    const token = await tokenProvider(undefined)
    return token || null
  } catch (error) {
    console.error('[TokenBridge] Error getting userinfo token:', error)
    return null
  }
}

/**
 * Get Access Token (API resource token for backend)
 */
export async function getAccessToken(): Promise<string | null> {
  if (!tokenProvider) {
    console.warn('[TokenBridge] No token provider set (dev mode or not authenticated)')
    return null
  }

  try {
    console.log(`[TokenBridge] Requesting token with resource=${apiResource || '(none)'}`)
    const token = await tokenProvider(apiResource || undefined)
    if (token) {
      // 解析 JWT payload 用于调试
      try {
        const parts = token.split('.')
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]))
          console.log('[TokenBridge] Token obtained:', {
            sub: payload.sub,
            iss: payload.iss,
            aud: payload.aud,
            exp: payload.exp,
            iat: payload.iat,
            scope: payload.scope,
            tokenPrefix: token.substring(0, 20) + '...',
          })
        } else {
          console.warn('[TokenBridge] Token is NOT a JWT (opaque token):', token.substring(0, 20))
        }
      } catch {
        console.warn('[TokenBridge] Could not decode token for logging')
      }
    } else {
      console.warn('[TokenBridge] Token provider returned empty token')
    }
    return token || null
  } catch (error) {
    console.error('[TokenBridge] Error getting access token:', error)
    return null
  }
}

/**
 * Get auth headers for API requests
 */
export async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await getAccessToken()
  if (token) {
    return { Authorization: `Bearer ${token}` }
  }
  return {}
}

/**
 * Check if Logto token bridge is active
 */
export function isTokenBridgeActive(): boolean {
  return tokenProvider !== null
}
